import { format } from 'date-fns';
import { merge } from 'lodash';
import { DEFAULT_EXCLUDE_FALSY_TOOGLE, Model, TableOptions, Query, DEFAULT_HEATMAP_STATUS, DEFAULT_BENCHMARK_HEATMAP_TOOGLE, DateRangeValue, parseDate, FixedDateRange, DateRangeValueJson, LegacyDateRangeValueJson } from '@hub-components';

export interface DiscoveryJson {
  name: string,
  public_uuid: string,
  query?: {},
  options?: TableOptions
}
export class Discovery extends Model {
  id: string;
  name: string;
  owner: string;
  editors: string[];
  updatedAt: Date;
  createdAt: Date;
  query: any;
  options: TableOptions = {
    dataOptions: {
      excludeFalsy: DEFAULT_EXCLUDE_FALSY_TOOGLE,
    },
    heatmapOptions: {
      enableHeatmap: DEFAULT_HEATMAP_STATUS,
      benchmarkHeatmap: DEFAULT_BENCHMARK_HEATMAP_TOOGLE,
    },
    resultsPerPage: { value: 10 },
  };

  tableQueries: Query[];
  benchmarkQueries: Query[];
  shareUuid: string;
  publicUuid: string;
  permissions: any;

  constructor(obj: Partial<DiscoveryJson> = {}, schema = null) {
    super(obj);
    if (obj['query']) {
      const query = obj['query'];
      query['schema'] = schema;

      if ('startDate' in query['dates'] && 'endDate' in query['dates']) {
        const { startDate, endDate, ...rest } = query['dates'];
        query['dates'] = {
          ...rest,
          start_date: startDate,
          end_date: endDate,
        };
      }
      query['dates'] = this.parseDateRangeValue(query['dates']);

      if (query['benchmarkDateRange']) {
        query['benchmarkDateRange'] = this.parseDateRangeValue(query['benchmarkDateRange']);
      }

      if (obj['options']) {
        const options = {
          ...obj.options,
          dataOptions: {
            ...obj?.options?.dataOptions,
          },
          heatmapOptions: {
            enableHeatmap: obj.options?.heatmapOptions?.enableHeatmap ?? DEFAULT_HEATMAP_STATUS,
            benchmarkHeatmap: obj.options?.heatmapOptions?.benchmarkHeatmap ?? DEFAULT_BENCHMARK_HEATMAP_TOOGLE,
          }
        };

        this.options = merge(this.options, options);
      }
    }
  }

  asJson(): DiscoveryJson {
    const json = {
      name: this.name,
      public_uuid: this.publicUuid,
    };
    if (this.query) {
      json['query'] = {
        dataset: this.query.dataset,
        metrics: this.query.metrics,
        dimensions: this.query.dimensions,
        dimensionProperties: this.query.dimensionProperties,
        dates: this.jsonDateRangeValue(this.query.dates),
        order: this.query.order,
        benchmark: this.query.benchmark,
        benchmarkDateRange: this.query.benchmarkDateRange ? this.jsonDateRangeValue(this.query.benchmarkDateRange) : undefined,
        filters: this.query.filters,
        drilldowns: this.query.drilldowns,
        advFilters: this.query.advFilters,
        metricFilters: this.query.metricFilters,
      };
    }

    if (this.options) {
      json['options'] = this.options;
    }
    return json;
  }

  private parseDateRangeValue(range: DateRangeValueJson | LegacyDateRangeValueJson): DateRangeValue {
    if ('dynamic' in range && range['dynamic']) {
      return {
        type: 'dynamic',
        dynamic: range['dynamic'],
      };
    } else if (range.type === 'dynamic') {
      const match = (range['value'] || '').match(/^(this|last|yesterday)(?:(?:_(\d+))?_(day|week|month|year)s?)?$/);
      if (match) {
        return {
          type: 'dynamic',
          dynamic: { name: match[1], num: match[2] && +match[2], unit: <any> match[3] },
        };
      }
    } else if ('start_date' in range) {
      const startDate = range.start_date;
      const endDate = range.end_date;
      return {
        type: 'fixed',
        startDate: parseDate(startDate),
        endDate: parseDate(endDate),
      };
    } else {
      const [startDate, endDate] = range['value'].split('-', 2);
      return {
        type: 'fixed',
        startDate: parseDate(startDate),
        endDate: parseDate(endDate),
      };
    }
  }

  private jsonDateRangeValue(range: DateRangeValue): DateRangeValueJson & LegacyDateRangeValueJson {
    if ('dynamic' in range && range['dynamic']) {
      const { name, num, unit } = range['dynamic'];
      let value = name;
      if (num && num > 1) {
        value += `_${num}`;
      }
      if (unit) {
        value += `_${unit}`;
        if (num && num > 1) {
          value += 's';
        }
      }
      return {
        type: 'dynamic',
        value: value,
        dynamic: range['dynamic'],
      };
    } else {
      const { startDate, endDate } = <FixedDateRange> range;
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      return {
        type: 'fixed',
        value: `${startDateStr.replaceAll('-', '')}-${endDateStr.replaceAll('-', '')}`,
        start_date: startDateStr,
        end_date: endDateStr,
      };
    }
  }
}
